<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import AnalyzePlanButton from "@/components/canvas/analysis/AnalyzePlanButton.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Liste d'observations vide. Deux cas à ne pas confondre :
 * - le filtre masque tout : le message le dit (sinon on croit avoir perdu
 *   ses observations) et propose de l'effacer ;
 * - l'ouvrage n'a encore aucune observation : les deux façons d'en créer
 *   (à la main, ou par l'analyse IA tant qu'elle n'a pas tourné sur ce
 *   poste) sont proposées ici, centrées sous le message, plutôt qu'en pied.
 */

defineProps<{
  /** Vide à cause du filtre de type (des observations existent). */
  filtered: boolean;
  /** Nombre total d'observations de l'ouvrage (message du filtre). */
  total: number;
  /** Éléments sélectionnés sur le plan (rattachés d'office à la création). */
  selectionCount: number;
  /** Aucune analyse sur ce poste : le bouton Analyser est proposé. */
  canAnalyze: boolean;
  /** Hors ligne : la création est impossible. */
  disabled: boolean;
  /** Une création est en cours (l'observation s'ouvrira dans la liste). */
  creating?: boolean;
}>();

defineEmits<{
  (e: "create"): void;
  (e: "clear-filter"): void;
}>();

const { t } = useTranslation();
</script>

<template>
  <div class="text-center py-4">
    <PhosphorIcon
      :name="filtered ? 'funnel-simple' : 'binoculars'"
      :size="32"
      class="text-grey"
    />
    <p class="text-grey mt-2 mb-0">
      {{
        filtered
          ? t("canvas:observations.emptyFiltered", { count: total })
          : t("canvas:observations.empty")
      }}
    </p>

    <button
      v-if="filtered"
      type="button"
      class="clear-filter-btn mt-2"
      @click="$emit('clear-filter')"
    >
      {{ t("canvas:observations.clearFilter") }}
    </button>

    <div v-else class="empty-actions d-flex flex-column gap-2 mx-auto mt-3">
      <DRBtn
        icon="bi-plus-lg"
        :label="
          selectionCount
            ? t('canvas:observations.createFromSelection', {
                count: selectionCount,
              })
            : t('canvas:observations.create')
        "
        variant="primary"
        size="sm"
        class="w-100"
        :loading="creating"
        :disabled="disabled"
        @click="$emit('create')"
      />
      <AnalyzePlanButton v-if="canAnalyze" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.empty-actions {
  max-width: 260px;
}

.clear-filter-btn {
  border: none;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  padding: 4px 10px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.18);
    color: #fff;
  }
}
</style>
